import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { setSideNav } from '../../../actions';
import styled, { css } from 'styled-components/macro';
import { MenuIcon } from './menuIcon/MenuIcon';
import { ClicLogo } from './clicLogo/ClicLogo';
import { SearchInput as SearchInputV2 } from './searchInput/SearchInput';
import { Avatar } from '../avatar/Avatar';
import { ProfileDropDown } from './profileDropdown/ProfileDropDown';
import { Button } from '../button/Button';
import { SideNavigation } from '../navigation/sideNavigation/SideNavigation';
import { device } from '../../Theme';
import { useDebouncedCallback } from 'use-debounce';
import { grabCurrentPath } from '../../../helpers';
import { Icon as IconV2 } from '../icon/Icon';
import { Link } from 'react-router-dom';
import {
  PATH_SIGN_IN,
  PATH_SIGN_UP,
  PATH_FORGOT,
  PATH_FORGOT_UPDATE,
} from '../../../constants';
import { rgba } from 'polished';

function Header({
  className,
  auth,
  strings,
  changeLang,
  channels,
  categories,
  genres,
  currentProfileImg,
  profileId,
  profileData,
  currentPath,
  setProfile,
  signOutUser,
  updateSearchKeyword,
  history,
  location,
  searchKeyword,
  submitSearchRequest,
  isSideNavToggled,
  setSideNav,
  isGlobalNavHidden,
  ...props
}) {
  const buttonRef = useRef(null);
  const searchFormRef = useRef();
  const sideNavigationRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const isSignInUp =
    grabCurrentPath() === PATH_SIGN_IN ||
    grabCurrentPath() === PATH_SIGN_UP ||
    grabCurrentPath() === PATH_FORGOT ||
    grabCurrentPath().includes(PATH_FORGOT_UPDATE) ||
    grabCurrentPath() === '/tv' ||
    grabCurrentPath() === '/linc' ||
    grabCurrentPath() === '/link';
  const [width, setWidth] = useState(window.innerWidth);
  const [lastWidth, setLastWidth] = useState(width);
  const [prevUrl, setPrevUrl] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [hasResized, setHasResized] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1024;

  const updateDimensions = useDebouncedCallback(() => {
    setLastWidth(width);
    setWidth(window.innerWidth);
    setHasResized(true);
  }, 200);

  const handleToggleClose = () => {
    setIsSidebarOpen(false);
    setIsProfileOpen(false);
  };

  const handleProfileToggle = (event) => {
    event?.stopPropagation();
    setIsProfileOpen((prev) => !prev);
    if (isSidebarOpen && isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleSidebarToggle = (event) => {
    event.stopPropagation();
    setIsSidebarOpen((prev) => !prev);
    if (isProfileOpen) {
      setIsProfileOpen(false);
    }
    setSideNav(!isSideNavToggled);
  };

  const pointBreak = () => {
    if (lastWidth < 768) {
      return width > 767;
    }
    if (lastWidth >= 768 && lastWidth < 1024) {
      return isSmallScreen || width > 1023;
    }
    return width < 1024;
  };
  const handleScreenDimensions = useDebouncedCallback(() => {
    if (hasResized && pointBreak()) {
      if (isSmallScreen || isMediumScreen) {
        setIsSidebarOpen(false);
        setSideNav(false);
      } else {
        setIsSidebarOpen(true);
        setSideNav(true);
      }
    } else if (isSmallScreen || isMediumScreen) {
      setIsSidebarOpen(false);
      setSideNav(false);
    } else {
      setIsSidebarOpen(true);
      setSideNav(true);
    }
  });

  const handleSubmit = useDebouncedCallback((keyword) => {
    history.push('/search?s=' + keyword);
  }, 400);
  const handleChange = useDebouncedCallback((event) => {
    const { value } = event?.target;
    if (value.length < 1) {
      history.push(prevUrl);
    } else {
      submitSearchRequest(value);
      history.push({ pathname: '/search', search: `?s=${value}` });
      updateSearchKeyword(value);
    }
  }, 400);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleClear = () => {
    updateSearchKeyword('');
    history.push(prevUrl);
  };

  useEffect(() => {
    const handleFirstTab = (event) => {
      if (
        event.key === 'Tab' &&
        document.activeElement === document.body &&
        !location.hash.includes('#main')
      ) {
        setTimeout(() => {
          buttonRef.current && buttonRef.current.focus();
        }, 0);
        window.removeEventListener('keydown', handleFirstTab);
      }
    };

    window.addEventListener('keydown', handleFirstTab);
    return () => {
      window.removeEventListener('keydown', handleFirstTab);
    };
  }, [location]);

  useEffect(() => {
    const htmlEl = document.documentElement;
    htmlEl.classList.toggle('nav--open', isSidebarOpen);
    htmlEl.classList.toggle('nav--closed', !isSidebarOpen);
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideProfile =
        profileDropdownRef.current &&
        profileDropdownRef.current.contains(event.target);
      const clickedInsideSidebar =
        sideNavigationRef.current &&
        sideNavigationRef.current.contains(event.target);

      if (isProfileOpen && !clickedInsideProfile) {
        setIsProfileOpen(false);
      }
      if (isSidebarOpen && !clickedInsideSidebar && isMobile) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isMobile, isProfileOpen, isSidebarOpen]);

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [updateDimensions]);

  useEffect(() => {
    handleScreenDimensions();
  }, [handleScreenDimensions, width]);

  useEffect(() => {
    if (location.pathname !== '/search') {
      if (!isMobile && !isSignInUp) {
        setPrevUrl(location);
        searchFormRef.current.clearSearchInput();
      }
    }
  }, [location, isMobile, isSignInUp]);

  useEffect(() => {
    setIsMobile(isSmallScreen);
  }, [width, isSmallScreen]);

  return !isSignInUp ? (
    <>
      <ClicHeader className={className} isGlobalNavHidden={isGlobalNavHidden}>
        <ClicHeaderContainerDefault>
          <ClicHeaderLeft>
            <MenuIcon ref={buttonRef} onClick={handleSidebarToggle} />
            <ClicLogo link="/" />
          </ClicHeaderLeft>
          <ClicHeaderCenter>
            {!isMobile ? (
              <SearchInputV2
                ref={searchFormRef}
                id="clic-search-header"
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                handleFocus={handleFocus}
                handleClear={handleClear}
                isHeader={true}
                hasSearchKeyword={searchKeyword.length > 0}
                strings={strings}
                autoFocus={searchKeyword || isFocused}
              />
            ) : (
              <Link to="/search">
                <IconV2 materialLabel="search" size="x-large" type="material" />
              </Link>
            )}
          </ClicHeaderCenter>
          <ClicHeaderRight>
            {auth && (
              <>
                <AvatarButton
                  lightTheme
                  type="button"
                  onClick={handleProfileToggle}
                  aria-label={strings.profileDropdown}
                >
                  <Avatar
                    currentProfileImg={currentProfileImg}
                    altText={strings.profileAvatar}
                  />
                </AvatarButton>
                <ProfileDropDown
                  ref={profileDropdownRef}
                  auth={auth}
                  open={isProfileOpen}
                  close={handleProfileToggle}
                  strings={strings}
                  profileId={profileId}
                  profileData={profileData}
                  setProfile={setProfile}
                  signOutUser={signOutUser}
                />
              </>
            )}
            <LangButton>
              <Button
                label={strings ? strings.langTextSwitch : 'Cymraeg'}
                onClick={() => changeLang()}
                size="small"
                type="secondary"
              />
            </LangButton>
            {!auth && (
              <SignInButton>
                <Button
                  label={strings.logIn}
                  link={PATH_SIGN_IN}
                  size="small"
                  loginRedirect={location.pathname}
                />
              </SignInButton>
            )}
          </ClicHeaderRight>
          <SideNavigation
            ref={sideNavigationRef}
            auth={auth}
            strings={strings}
            open={isSidebarOpen}
            close={handleToggleClose}
            channels={channels}
            categories={categories}
            genres={genres}
            changeLang={() => changeLang()}
            currentPath={currentPath}
            width={width}
            isMobile={isMobile}
          />
        </ClicHeaderContainerDefault>
      </ClicHeader>
      {!auth && (
        <SignInButtonMobile>
          <ButtonOutlined
            label={strings.logIn}
            link={PATH_SIGN_IN}
            size="small"
            loginRedirect={location.pathname}
          />
        </SignInButtonMobile>
      )}
    </>
  ) : (
    <ClicHeaderSignIn className={className} shouldHideNav={isSignInUp}>
      <ClicHeaderContainer>
        <ClicHeaderLeft>
          <ClicLogo link="/" />
        </ClicHeaderLeft>
        <ClicHeaderRight>
          <LangButton2>
            <Button
              label={strings ? strings.langTextSwitch : 'Cymraeg'}
              onClick={() => changeLang()}
              size="small"
              type="secondary"
            />
          </LangButton2>
          <CloseSignInButton onClick={() => history.push(prevUrl)}>
            <IconV2 customLabel="close" size="small" />
          </CloseSignInButton>
        </ClicHeaderRight>
      </ClicHeaderContainer>
    </ClicHeaderSignIn>
  );
}

const ClicHeader = styled.header`
  background: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.whiteFull
      : props.theme.color.blackFull};
  position: absolute;
  top: 68px;
  left: 0;
  width: 100%;
  z-index: 100;
  @media ${device.tablet} {
    top: 68px;
  }
  @media ${device.desktop} {
    top: 76px;
  }
  ${(props) =>
    props.isGlobalNavHidden &&
    css`
      position: fixed;
      top: 0;
      z-index: 11;
      @media ${device.tablet} {
        top: 0;
      }
    `};
`;

const ClicHeaderSignIn = styled(ClicHeader)`
  background: none;
  ${(props) =>
    props.shouldHideNav &&
    css`
      top: 0;
    `}
`;

const ClicHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  @media ${device.tablet} {
    justify-content: space-between;
  }
`;

const ClicHeaderContainerDefault = styled(ClicHeaderContainer)``;

const ClicHeaderInner = styled.div`
  display: flex;
  height: 69px;
  z-index: 0;
  @media ${device.tablet} {
    padding: 0;
    height: 97px;
  }
`;

const LangButton = styled.div``;

const LangButton2 = styled.div`
  margin-right: 20px;
  a {
    border-color: transparent;
    font-size: 16px;
  }
`;

const SignInButton = styled.div`
  display: none;
  margin-left: 20px;
  @media ${device.tablet} {
    display: flex;
  }
`;

const SignInButtonMobile = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 149px;
  padding: 0 15px 6px;
  position: relative;
  @media ${device.tablet} {
    display: none;
  }
`;

const ButtonOutlined = styled(Button)`
  border-radius: 100vw;
  box-shadow: 0 0 0 10px
    ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.whiteFull
        : props.theme.color.blackFull};
`;

const ClicHeaderLeft = styled(ClicHeaderInner)`
  position: relative;
  padding-left: 14px;
  align-items: center;
  flex: 1;
  @media ${device.tablet} {
    padding-left: 26px;
    flex: none;
  }
  .clic-sidebar {
    top: 64px;
    left: 0;
    @media ${device.tablet} {
      top: 100px;
    }
  }
`;

const ClicHeaderCenter = styled(ClicHeaderInner)`
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  padding-right: 15px;
  div {
    box-shadow: none;
    align-items: center;
    &:hover {
      box-shadow: none;
    }
  }
  input {
    display: none;
    @media ${device.tablet} {
      display: flex;
    }
  }
  button {
    border-radius: 4px;
  }
  a {
    display: flex;
    width: 32px;
    height: 32px;
    align-items: center;
    box-shadow: none;
    transition: none;
    &:hover {
      outline: none;
      box-shadow: none !important;
    }
    &:focus-visible {
      border-radius: 4px;
      outline: 2px solid
        ${(props) =>
          props.theme.name === 'light'
            ? props.theme.color.statusLightFocus
            : props.theme.color.statusDarkFocus};
    }
    &:focus:not(:focus-visible) {
      outline: none;
    }
  }
  @media ${device.tablet} {
    padding-right: 0;
    justify-content: center;
    > div {
      box-shadow: ${(props) =>
        props.theme.name === 'light'
          ? `0px 0px 0px 1px ${props.theme.color.black10}`
          : `0px 0px 0px 1px ${rgba(props.theme.color.whiteFull, 0.2)}`};
      align-items: initial;
      border-radius: 8px;
      &:hover {
        box-shadow: ${(props) =>
          props.theme.name === 'light'
            ? `0px 0px 0px 1px ${props.theme.color.black10}`
            : `0px 0px 0px 1px ${rgba(props.theme.color.whiteFull, 0.2)}`};
      }
    }
    input {
      display: inline-block;
    }
    button {
      border-radius: 4px;
    }
  }
`;

const ClicHeaderRight = styled(ClicHeaderInner)`
  position: relative;
  align-items: center;
  padding-right: 15px;
  @media ${device.tablet} {
    padding-right: 26px;
    padding-left: 0;
  }
  ${LangButton} {
    display: none;
    @media ${device.tablet} {
      display: flex;
      margin-left: 20px;
    }
    @media ${device.desktop} {
      display: none;
    }
  }
  nav,
  + nav {
    top: 68px;
    overflow-y: auto;
    @media ${device.tablet} {
      top: 97px;
      width: 100%;
    }
    @media ${device.tablet} {
      padding-right: 14px;
    }
  }
`;

const AvatarButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  background: none;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light'
        ? 'transparent'
        : `${rgba(props.theme.color.whiteFull, 0.01)}`};
  border-radius: 50px;
  cursor: pointer;
  img {
    width: 28px;
    height: 28px;
    @media ${device.tablet} {
      width: 50px;
      height: 50px;
    }
  }
  + nav {
    position: absolute;
    width: 307px;
    top: 90%;
    right: 12%;
    @media ${device.tablet} {
      right: 30%;
    }
  }
  &:hover {
    border: 1px solid
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.black10
          : `${rgba(props.theme.color.whiteFull, 0.2)}`};
  }
  &:focus-visible {
    border: 2px solid
      ${(props) =>
        props.theme.name === 'light'
          ? `${rgba(props.theme.color.blackFull, 0.01)}`
          : `${rgba(props.theme.color.whiteFull, 0.01)}`};
    outline: 2px solid
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.statusLightFocus
          : props.theme.color.statusDarkFocus};
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const CloseSignInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 16px;
  height: 16px;
  width: 40px;
  height: 40px;
  background: ${(props) =>
    props.theme.name === 'light'
      ? 'rgba(251, 251, 251, 0.6);'
      : 'rgba(16, 16, 15, 0.6)'};
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0px 32px 64px rgba(0, 0, 0, 0.075), 0px 16px 32px rgba(0, 0, 0, 0.075), 0px 8px 16px rgba(0, 0, 0, 0.075), 0px 4px 8px rgba(0, 0, 0, 0.075);'
      : null};
  backdrop-filter: blur(24px);
  border-radius: 100px;
  border: 1px solid transparent;
  outline: none;
  cursor: pointer;
  @media ${device.tablet} {
    width: 60px;
    height: 60px;
  }
  span {
    width: 16px;
    height: 16px;
    background-color: ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.primaryAAA
        : props.theme.color.primaryFull};
    @media ${device.tablet} {
      width: 20px;
      height: 20px;
    }
  }
  &:focus-visible {
    outline: 2px solid
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.statusLightFocus
          : props.theme.color.statusDarkFocus};
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &:hover {
    border: 1px solid rgba(251, 251, 251, 0.65);
  }
`;

function mapStateToProps(state) {
  return {
    isSideNavToggled: state.sideNav,
  };
}

export default connect(mapStateToProps, { setSideNav })(Header);
